import { NudosButton, NudosGenericModal, NudosModalContent } from '../../../../../../../components/NudosComponents';
import { IconBuilding, IconEmployee, IconHouse } from '../../../../../../../assets/DesignSystem/SVGComponents';
import { FC, useState } from 'react';
import { IOptionsReportList, IReportData, IShipmentData } from '../../../../../../../types/orders';
import { sendWrongAddressReport } from '../../../../../../../services/orders';
import errorMessage from '../../../../../../../utils/errorMessage';
import { getNudosData } from '../../../../../../../utils/getNudosData';
import { displaySuccessNotification } from '../../../../../../../utils/displayNudosStandardNotifications';
import { LOCATION_PLACE_TYPE } from '../../../../../../../types/services';
import './ConfirmEditModal.scss';

interface IConfirmEditModal {
  closeModal: () => void;
  selectedOptions: IOptionsReportList[];
  orderId: number;
  shipInfo: IShipmentData;
  clearSelectedList: () => void;
  updateWithoutLoaders: () => void;
}

const ConfirmEditModal: FC<IConfirmEditModal> = ({
  closeModal,
  selectedOptions,
  orderId,
  shipInfo,
  clearSelectedList,
  updateWithoutLoaders
}: IConfirmEditModal) => {
  const userId = Number(getNudosData()?.userData?.id);
  const selectedCity = !!selectedOptions.find(option => option.value === 'city');
  const selectPhone = !!selectedOptions.find(option => option.value === 'phoneNumber');
  const selectAddress = !!selectedOptions.find(option => option.value === 'address');
  const selectAdditionalReferences = !!selectedOptions.find(option => option.value === 'additionalReferences');
  const selectZipCode = !!selectedOptions.find(option => option.value === 'zipCode');

  const [loading, setLoading] = useState<boolean>(false);
  const closeExternalModal = () => {
    if (!loading) {
      closeModal();
    }
  };

  const sendReport = async () => {
    const options: string[] = [];
    selectedOptions.forEach((option: IOptionsReportList) => {
      options.push(option.value);
    });
    setLoading(true);
    const body: IReportData = {
      orderId,
      shipmentId: shipInfo?.shipmentId,
      userId: userId,
      wrongAttributes: options
    };
    try {
      await sendWrongAddressReport(body);
      await updateWithoutLoaders();
      displaySuccessNotification({
        customJSXMessage: <>Reporte del problema con la información enviado con éxito.</>
      });
      clearSelectedList();
      closeModal();
    } catch (error) {
      errorMessage();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <NudosGenericModal
        closeModalCallback={() => {
          closeExternalModal();
        }}
        clickOutsideCallback={() => {
          closeExternalModal();
        }}
        modalContent={
          <NudosModalContent
            noScroll
            toCloseModal={() => {
              closeExternalModal();
            }}
            customHeight="426px"
            customWidth="580px"
            modalContentTitle="Información incorrecta/incompleta"
            CustomModalComponent={
              <div id="confirmEditModal">
                <div className="subtitleContainer">
                  Estás a punto de enviar un correo y notificar al cliente que la información de <br />
                  destino está incorrecta o incompleta
                </div>
                <div className="cardContainer">
                  <div className="destinationDetailCard">
                    <div className="destinationHeader">
                      <div className="destinationAddressDetails">
                        <div className="destinationIcon">
                          <div className="iconBoxContainer">
                            <div className="iconBox">
                              {shipInfo?.place === LOCATION_PLACE_TYPE.OFFICE ? <IconBuilding /> : <IconEmployee />}
                            </div>
                          </div>
                          <div
                            className="flagBox"
                            style={{
                              backgroundImage: `url(${shipInfo?.country?.flag})`
                            }}
                          ></div>
                        </div>
                        {shipInfo?.place === LOCATION_PLACE_TYPE.USER ? (
                          <div className="destinationText">{shipInfo.receiverInfo.receiverName}</div>
                        ) : (
                          <div className="destinationText">{`Oficinas ${shipInfo?.country?.name}`}</div>
                        )}
                      </div>
                      <div className="destinationAddress">Información de destino</div>
                    </div>
                    <div className="grayLine" />
                    {(selectedCity || selectZipCode || selectPhone) && (
                      <div className="basicInformationContainer">
                        {selectedCity && (
                          <div>
                            <div className="grayTitle">Ciudad</div>
                            <div className="infoValue">{shipInfo?.city} </div>
                          </div>
                        )}
                        {selectZipCode && (
                          <div>
                            <div className="grayTitle">Código postal</div>
                            <div className="infoValue">{shipInfo.zipCode}</div>
                          </div>
                        )}
                        {selectPhone && (
                          <div>
                            <div className="grayTitle">Número de teléfono</div>
                            <div className="infoValue">{shipInfo?.receiverInfo?.receiverPhone}</div>
                          </div>
                        )}
                      </div>
                    )}
                    {selectAddress && (
                      <div className="infoAddressContainer">
                        <div className="title">Dirección</div>
                        <div className="infoItems">
                          <div className="iconInfoContianer">
                            <IconHouse />
                          </div>
                          <div className="info">{shipInfo?.deliveryAddress}</div>
                        </div>
                      </div>
                    )}
                    {selectAdditionalReferences && (
                      <div className="additionalContainer">
                        <div className="title">Indicaciones</div>
                        <div className="textIndications">{shipInfo?.additionalReferences} </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="buttonsContainer">
                  <div className="subtitleContainer buttonsSubtitle">¿Seguro que deseas continuar?</div>
                  <div className="modalButtons">
                    <NudosButton
                      customHeight="38px"
                      componentSize="small"
                      colorPalette="secondary"
                      buttonText="Cancelar"
                      handleClick={() => {
                        closeModal();
                      }}
                      isButtonDisabled={loading}
                    />
                    <NudosButton
                      customHeight="38px"
                      componentSize="small"
                      buttonText="Enviar Correo"
                      isButtonLoading={loading}
                      handleClick={() => {
                        sendReport();
                      }}
                    />
                  </div>
                </div>
              </div>
            }
          />
        }
      />
    </>
  );
};

export default ConfirmEditModal;
